import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import SEO, { collectionSchema } from "@/components/SEO";

export default function RegionsListPage() {
  const [regions, setRegions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([api.get("/regions"), api.get("/projects")])
      .then(([r, p]) => {
        if (!mounted) return;
        if (Array.isArray(r.data)) setRegions(r.data);
        if (Array.isArray(p.data)) setProjects(p.data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const projectCountByRegion = projects.reduce((acc, p) => {
    if (p.region) acc[p.region] = (acc[p.region] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="regions-list-page">
      <SEO
        title="Regions"
        description="Province- and territory-level intelligence — key commodities, infrastructure notes, policy context, and active project counts across Canada's mining jurisdictions."
        jsonLd={collectionSchema("Regions", "Canadian mining jurisdictions", "/regions")}
      />
      <section className="bg-white border-b border-border">
        <div className="bb-container py-12 lg:py-16">
          <div className="bb-overline text-bb-copper">Jurisdictions</div>
          <h1
            className="bb-headline mt-4 text-4xl sm:text-5xl lg:text-6xl text-bb-navy"
            data-testid="regions-page-headline"
          >
            Regions
          </h1>
          <p className="mt-5 text-base lg:text-lg text-bb-slate max-w-2xl leading-relaxed">
            Province- and territory-level intelligence — key commodities,
            infrastructure notes, policy context, and active project counts
            across Canada&rsquo;s mining jurisdictions.
          </p>
          <div className="mt-6 bb-overline text-bb-slate">
            {regions.length} jurisdictions
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="bb-container">
          {loading ? (
            <div className="text-center py-20 bb-overline text-bb-slate">
              Loading regions…
            </div>
          ) : (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid="regions-grid"
            >
              {regions.map((r) => (
                <Link
                  key={r.id}
                  to={`/regions/${r.slug}`}
                  className="bb-card p-6 flex flex-col gap-3 group"
                  data-testid={`region-card-${r.slug}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-bb-offwhite text-bb-navy border border-border">
                      <MapPin size={18} strokeWidth={1.8} />
                    </span>
                    <span className="font-mono text-[11px] text-bb-slate">
                      {projectCountByRegion[r.id] || 0} project
                      {(projectCountByRegion[r.id] || 0) === 1 ? "" : "s"}
                    </span>
                  </div>
                  <h3 className="font-archivo text-xl text-bb-navy font-semibold tracking-tight leading-tight">
                    <span className="bb-link">{r.name}</span>
                  </h3>
                  <p className="text-sm text-bb-slate leading-relaxed line-clamp-4">
                    {r.summary}
                  </p>
                  <div className="mt-auto pt-4 border-t border-border flex items-center justify-between">
                    <div className="flex flex-wrap gap-1.5">
                      {(r.keyCommodities || []).slice(0, 3).map((c) => (
                        <span
                          key={c}
                          className="bb-tag-neutral text-[10px] font-mono px-2 py-0.5"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                    <ArrowUpRight
                      size={14}
                      className="text-bb-navy group-hover:text-bb-copper transition-colors"
                    />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
